<script lang="ts">
    import { onMount, setContext, onDestroy, afterUpdate } from 'svelte'
    import { createAsyncItemStore } from '@/store'

    import CommentList from '@components/CommentList.svelte'
    import NewComment from '@components/NewComment.svelte'
    import EditComment from '@components/EditComment.svelte'
    import { getString } from '@/utils'

    export let contextid
    export let cmid = null
    export let ratingItems: any = {}
    export let commentBlock: any = {}
    export let title = null
    export let setRating = null
    export let modalId

    export let helpful
    export let comprehensible

    let editingComment = null
    let store
    let comments

    let ratingItemValues = { helpful, comprehensible }
    let ratingItemRefs = {}

    let storeUnsubscribe

    afterUpdate(() => {
        updateRatingCheckboxes()
    })

    function showModal(data: any) {
        window.M.theme_soon.events.emit('MODAL_OPEN', data)
    }

    function updateRatingCheckboxes() {
        if (ratingItemValues) {
            Object.keys(ratingItemValues).forEach(name => {
                const refName = `${name}-${ratingItemValues[name]}`

                if (ratingItemRefs[refName]) {
                    ratingItemRefs[refName].checked = true
                }
            })
        }
    }

    function createComment(comment) {
        store.create(comment)
    }

    function modifyComment(comment) {
        store.modify(comment)
    }

    function removeComment(comment) {

        const removeAction = () => {
            store.remove(comment)
        }

        showModal({
            title: getString('confirm'),
            content: getString('confirm_comment_delete', 'local_comments'),
            type: 'confirm',
            actions: [
                { name: 'confirm', type: 'danger', text: getString('confirm'), fn: (modal) => { removeAction(); modal.close() } },
                { name: 'abort', text: getString('cancel'), type: 'outline-primary', fn: (modal) => modal.close() }
            ]
        })
    }

    function editComment(comment) {
        if (comment) {
            window.M.theme_soon.events.emit('MODAL_LOCK', { id: modalId, text: getString('confirm_unsaved_close', 'local_comments') })
        }
        else {
            window.M.theme_soon.events.emit('MODAL_UNLOCK', { id: modalId })
        }

        editingComment = comment
    }

    function updateComments(data) {
        comments = data
    }

    setContext('createComment', createComment)
    setContext('editComment', editComment)
    setContext('modifyComment', modifyComment)
    setContext('removeComment', removeComment)
    setContext('modalId', modalId)

    const createStore = async () => {
        store = await createAsyncItemStore(
            `/local/comments/ajax.php?action=comment&contextid=${contextid}`
        )
        storeUnsubscribe = store.subscribe(updateComments)
    }

    const handleRatingChange = (e, item, rating) => {
        ratingConfirmation(e.currentTarget)

        ratingItemValues[item.name] = rating
        setRating(ratingItemValues)
    }

    function ratingConfirmation(elem) {
        const controlGroup = elem.closest('.control-group')

        const confirmElem = document.createElement('div')
        confirmElem.className = 'badge badge-primary rating-confirmation'
        confirmElem.innerHTML = getString('rating_saved_confirmation', 'local_rating')

        const oldConfirmElem = controlGroup.querySelector('.rating-confirmation')

        if (oldConfirmElem) {
            oldConfirmElem.parentElement.replaceChild(confirmElem, oldConfirmElem)
        }
        else {
            controlGroup.appendChild(confirmElem)
        }

        setTimeout(() => {
            confirmElem.remove()
        }, 4000)
    }

    onMount(async () => {
        createStore()
    })

    onDestroy(() => {
        storeUnsubscribe && storeUnsubscribe()
    })
</script>

{#if title}
    <h1 class="text-center mt-4 mb-0">{title}</h1>
{/if}

<form class="local_rating_form" method="post" action="/local/rating/rating.php">

    <svg style="display: none" class="emoticon" viewBox="0 0 24 24">
        <path id="emoticon-1" fill="currentColor" d="M12 2C6.47 2 2 6.5 2 12S6.47 22 12 22C17.5 22 22 17.5 22 12S17.5 2 12 2M12 20C7.58 20 4 16.42 4 12S7.58 4 12 4 20 7.58 20 12 16.42 20 12 20M15.5 11C16.33 11 17 10.33 17 9.5S16.33 8 15.5 8 14 8.67 14 9.5 14.67 11 15.5 11M8.5 11C9.33 11 10 10.33 10 9.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11M12 13.5C9.67 13.5 7.69 14.96 6.89 17H17.11C16.31 14.96 14.33 13.5 12 13.5Z" />
        <path id="emoticon-2" fill="currentColor" d="M20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12M22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2A10,10 0 0,1 22,12M15.5,8C16.3,8 17,8.7 17,9.5C17,10.3 16.3,11 15.5,11C14.7,11 14,10.3 14,9.5C14,8.7 14.7,8 15.5,8M10,9.5C10,10.3 9.3,11 8.5,11C7.7,11 7,10.3 7,9.5C7,8.7 7.7,8 8.5,8C9.3,8 10,8.7 10,9.5M12,14C13.75,14 15.29,14.72 16.19,15.81L14.77,17.23C14.32,16.5 13.25,16 12,16C10.75,16 9.68,16.5 9.23,17.23L7.81,15.81C8.71,14.72 10.25,14 12,14Z" />
        <path id="emoticon-3" fill="currentColor" d="M20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12M22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2A10,10 0 0,1 22,12M10,9.5C10,10.3 9.3,11 8.5,11C7.7,11 7,10.3 7,9.5C7,8.7 7.7,8 8.5,8C9.3,8 10,8.7 10,9.5M17,9.5C17,10.3 16.3,11 15.5,11C14.7,11 14,10.3 14,9.5C14,8.7 14.7,8 15.5,8C16.3,8 17,8.7 17,9.5M12,17.23C10.25,17.23 8.71,16.5 7.81,15.42L9.23,14C9.68,14.72 10.75,15.23 12,15.23C13.25,15.23 14.32,14.72 14.77,14L16.19,15.42C15.29,16.5 13.75,17.23 12,17.23Z" />
        <path id="emoticon-4" fill="currentColor" d="M12,17.5C14.33,17.5 16.3,16.04 17.11,14H6.89C7.69,16.04 9.67,17.5 12,17.5M8.5,11A1.5,1.5 0 0,0 10,9.5A1.5,1.5 0 0,0 8.5,8A1.5,1.5 0 0,0 7,9.5A1.5,1.5 0 0,0 8.5,11M15.5,11A1.5,1.5 0 0,0 17,9.5A1.5,1.5 0 0,0 15.5,8A1.5,1.5 0 0,0 14,9.5A1.5,1.5 0 0,0 15.5,11M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
    </svg>

    <input type="hidden" name="id" value="{cmid}">
    <input type="hidden" name="sesskey" value={M.cfg.sesskey}>

    {#each Object.keys(ratingItems) as name (name)}
        <h4>{ratingItems[name].heading}</h4>

        {#if ratingItems[name].description}
            {@html ratingItems[name].description}
        {/if}

        <div class="control-group control-group--rating">

            {#each ratingItems[name].items as item}
                <div class="form-check form-check-inline">
                    <label class="form-check-label">
                        <input required class="form-check-input" type="radio" name={`rating-${name}`} bind:this={ratingItemRefs[`${name}-${item.num}`]} on:change={(e) => handleRatingChange(e, ratingItems[name], item.num)} class:checked={item.isChecked}>
                        <span class="form-check-label">
                            <svg class="emoticon" viewBox="0 0 24 24" xmlns:xlink="http://www.w3.org/1999/xlink">
                                <use href="#emoticon-{item.num}"></use>
                            </svg>
                            {item.label}
                        </span>
                    </label>
                </div>
            {/each}

        </div>
    {/each}

</form>

<div class="comments-block">
    {#if commentBlock.title}
        <h2>{@html commentBlock.title}</h2>
    {:else}
        <h2>Kommentare</h2>
    {/if}

    {#if commentBlock.description}
        {@html commentBlock.description}
    {/if}

    {#if editingComment}
        <EditComment comment={editingComment} />
    {:else}
        <NewComment />

        {#if !comments || comments === null}
            <p>Kommentare werden geladen...</p>
        {:else}
            <CommentList comments={comments} />
        {/if}
    {/if}
</div>
